import { Component, ViewChild, inject,
    ChangeDetectionStrategy
} from '@angular/core';
import { Tree } from '@openng/optimus-ui/tree';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { GuiUtils } from "src/modules/utils/misc/gui-utils";
import { TreeSelectionHelper } from 'src/modules/utils/misc/tree-selection-helper';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { Objects } from "src/modules/utils/shared/objects";
import { Utils } from 'src/modules/utils/shared/utils';
import { EnaioCertificateService } from '../../services/enaio-certificate.service';
import { ClassRegisterSettings } from '../../shared/class-register-settings.entity';
import { CourseParticipant } from '../../shared/course-participant.entity';
import { Course } from '../../shared/course.entity';
import { EnaioCertificatesTools } from '../../shared/enaio-certificates-tools';
import { Pupil } from '../../shared/pupil.model';
import { SchoolType } from '../../shared/school-type.enum';
import { Teacher } from '../../shared/teacher.model';

@Component({
    selector: 'app-edit-class-course',
    templateUrl: './edit-class-course.component.html',
    styleUrls: ['./edit-class-course.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditClassCourseComponent extends ComponentView {

    service = inject(EnaioCertificateService);

    neededParams: ObjectOfSimpleDataTypes = { year: "number?", id: "number" };
    year: number;
    id: number;

    item: Course;
    form: FrontendForm;
    pupils: Pupil[];
    teachers: Teacher[];
    settings: ClassRegisterSettings;

    pupilsTree: TreeSelectionHelper;

    @ViewChild("#tree") tree: Tree;

    async initParams(): Promise<boolean> {
        this.fullScreenComponent = false;
        if (this.year == null) {
            this.year = EnaioCertificatesTools.getCurrentYear();
        }
        this.item = this.id == 0 ? new Course() : await RestEndpoint.main().findById(this.id).run("api/cer/classregister/course").get(Course);
        if (this.item == null) {
            await this.app.dialog.info("Der Kurs wurde nicht gefunden. Möglicherweise wurde er inzwischen gelöscht.", "Kurs nicht verfügbar");
            await this.app.navigateTo(["/enaio-certificates", "class-registers", "courses", this.year]);
            return false;
        }
        if (this.item.participants == null) {
            this.item.participants = [];
        }

        if (this.pupils == null) {
            this.pupils = await this.service.restGetPupilsForYear(EnaioCertificatesTools.getSchoolYearFromYear(this.year));
        }
        if (this.teachers == null) {
            this.teachers = await this.service.restGetTeachers();
        }
        if (this.settings == null) {
            this.settings = await this.service.restGetClassRegisterSettings(this.year, false); //Warum konstant false?
        }

        this.form = new FrontendForm([
            new FrontendField("type", "Schulzweig", FrontendFieldType.dropdown, {
                mandatory: true,
                listItems: Arrays.mapWithIndex(EnaioCertificatesTools.getSchoolTypeNames(), (n, index) => new FrontendFieldListItem(index, n)),
                onValueChanged: this.changeSchoolType.bind(this)
            }),
            new FrontendField("title", "Bezeichnung", FrontendFieldType.string, { mandatory: true }),
            new FrontendField("group", "Gruppe", FrontendFieldType.string),
            new FrontendField("instructor", "Kursleiter", FrontendFieldType.dropdown, {
                mandatory: true,
                listItems: FrontendFieldListItem.make(this.teachers, item => [item.userName, item.getDisplayName()])
            }),
            new FrontendField("endDate", "Enddatum", FrontendFieldType.date),
            new FrontendField("forCertificate", "Zeugnisrelevant", FrontendFieldType.bool, {
                onValueChanged: this.changeForCertificate.bind(this)
            }),
            new FrontendField("titleForCertificate", "Abweichende Bezeichnung im Zeugnis", FrontendFieldType.string, { indent: 5 }),
            new FrontendField("isLeveledCourse", "Verwendet Anspruchsebene", FrontendFieldType.bool, { onValueChanged: this.isLeveledCourseValueChanged.bind(this)}),
            new FrontendField("levelDomain", "Fach", FrontendFieldType.string, { mandatory: true, indent: 5, onValueChanged: this.levelFieldValueChanged.bind(this)}),
            new FrontendField("levelType", "Anspruchsebene", FrontendFieldType.dropdown, { 
                mandatory: true, 
                listItems: ["", ...EnaioCertificatesTools.getLevelTypes()].map(type => new FrontendFieldListItem(type, type)), indent: 5, onValueChanged: this.levelFieldValueChanged.bind(this)
            }),
            new FrontendField("levelIndex", "Index (0 = ohne)", FrontendFieldType.int, { mandatory: true, indent: 5, onValueChanged: this.levelFieldValueChanged.bind(this)}),
            new FrontendField("forGrades", "Klassenstufen", FrontendFieldType.listBox, {
                listBoxCheckBoxes: true,
                listItems: Utils.getRange(1, 13).map(g => new FrontendFieldListItem(g, "Klassenstufe " + g)),
                onValueChanged: this.updatePupilsTree.bind(this)
            })
        ]);

        this.pupilsTree = new TreeSelectionHelper(this.tree);

        this.form.fill(this.item);

        this.updatePupilsTree();
        this.pupilsTree.setSelected(this.item.participants.map(p => p.pupilId));

        this.isLeveledCourseValueChanged();
        this.changeSchoolType();
        this.changeForCertificate();

        this.app.updateNavigation("Kursbuch", [
            this.service.getHomeCourses(),
            { label: this.item.id == 0 ? "Neu" : this.item.title, routerLink: ["/enaio-certificates", "class-registers", "course", this.item.id] }
        ]);
        return true;
    }

    isLeveledCourseValueChanged(): void {
        let value = this.form.getValueBool("isLeveledCourse");
        this.form.getField("levelDomain").visible = value;
        this.form.getField("levelType").visible = value;
        this.form.getField("levelIndex").visible = value;
        this.form.getField("title").enabled = !value;
        this.levelFieldValueChanged();
    }

    levelFieldValueChanged(): void {
        if (this.form.getValue("isLeveledCourse")) {
            this.form.setValue("title", this.form.getValueString("levelDomain") + " " + Arrays.itemsToString(this.form.getValue("forGrades") as number[], "/") + " " + this.form.getValue("levelType") + (this.form.getValueNumber("levelIndex") > 0 ? " " + this.form.getValueNumber("levelIndex") : ""));
        }
    }

    changeForCertificate(): void {
        this.form.getField("titleForCertificate").visible = this.form.getValueBool("forCertificate");
    }

    changeSchoolType(): void {
        let type = this.form.getValue("type") as SchoolType;
        this.form.getField("instructor").listItems = this.teachers.filter(t => t.isFor(type, this.settings)).map(t => new FrontendFieldListItem(t.userName, t.getDisplayName()));
    }

    updatePupilsTree(): void {
        if (this.pupils == null) {
            return;
        }
        let sel = (this.form.getValue("forGrades") as number[]) ?? [];

        this.pupilsTree.setItems(GuiUtils.generateTree2(this.pupils.filter(p => sel.includes(p.grade) && p.title != ""), true, false, (p, g, m) => [
            g("1" + p.grade, () => m("Klassenstufe " + p.grade, null, { expanded: true })),
            g("2" + p.grade + p.title, () => m(p.getClassDisplayName())),
            g("3" + p.id, () => m(p.getFullName(), p.id))
        ]), true);
    }

    async save(): Promise<void> {
        if (!this.app.validateForm(this.form)) {
            return;
        }

        this.form.get(this.item);
        if (this.item.id == 0) {
            this.item.year = this.year;
            this.item.isHalfYear = false;
        }
        this.item.participants = this.pupilsTree.getSelected().filter(id => Utils.isNumber(id)).map(id => Objects.fromPlain(CourseParticipant, { pupilId: id}));
        await this.app.saveDataHandler(async () => RestEndpoint.main().upsert(this.item).run("api/cer/classregister/course").getText(), this, true, true);
    }

    cancel(): void {
        this.app.navigateBack();
    }
}
